/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcVehicleSystemsIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import VehicleSystems from "../../../pages/labcar/VehicleSystems";
import VehicleComponents from "../../../pages/labcar/VehicleComponents";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../../redux/actions";
import { selectVehicleSystemAction } from "../../../redux/actions/labCarActions";

function VlcVehicleSystemsIndex({
  drawerWidth = 240,
  drawerOpen = true,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  variantOptionList = [],
  vsAndVCOptionsList = [],
  getZoneVSCountList: getZoneVSCountListProp,
}) {
  const dispatch = useDispatch();

  // states
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);
  // Local state for filters

  // Shared filter state (mirrors props expected by VehicleSystems/VehicleComponents)
  const [filterState, setFilterState] = useState({
    sortField: "created_at",
    sortOrder: "ASC",
    nameFilter: [],
    assemblyNumFilter: [],
    descriptionFilter: [],
    nameFilterSearch: "",
    assemblyNumFilterSearch: "",
    descriptionFilterSearch: "",
  });

  const onFilterChange = (searchField, searchValue) => {
    setFilterState((prev) => ({
      ...prev,
      [searchField]: searchValue,
    }));
  };

  const onClearFilters = () => {
    setFilterState({
      sortField: "created_at",
      sortOrder: "ASC",
      nameFilter: [],
      assemblyNumFilter: [],
      descriptionFilter: [],
      nameFilterSearch: "",
      assemblyNumFilterSearch: "",
      descriptionFilterSearch: "",
    });
  };

  // Optional: Fetch zones VS count (basic implementation similar to VlcIndex)
  const getZoneVSCountList = useCallback(async () => {
    if (
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    )
      return;
    // Prefer parent-supplied function when available
    if (typeof getZoneVSCountListProp === "function") {
      try {
        await getZoneVSCountListProp();
      } catch (e) {
      }
    }
    try {
      const apiDetails = labCar.get_vehicle_systems_all;
      await dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
          },
        }),
      );
      // Container no longer renders counts; parent CarCard shows them.
    } catch (e) {
    }
  }, [
    dispatch,
    getZoneVSCountListProp,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
  ]);

  useEffect(() => {
    getZoneVSCountList();
  }, [getZoneVSCountList]);

  // Derive options lists from Redux settings
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  const selectedVehicleSystemDetails = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemDetails,
  );
  const settingsVehicleSystemsDetails = useSelector(
    (state) => state.vehicleSystemsReducer,
  );

  const vehicleSystemsMasterListLength =
    settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data?.length || 0;

  // Ensure the Vehicle System master list is loaded for the Add dialog dropdown.
  // (Previously it was only fetched when visiting EEComponents.)
  useEffect(() => {
    if (vehicleSystemsMasterListLength > 0) return;
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_vehicle_systems,
        show_toast: false,
      }),
    );
  }, [dispatch, vehicleSystemsMasterListLength]);

  const supplierOptionsList = useMemo(() => {
    const suppliers = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return Array.isArray(suppliers)
      ? suppliers.map((s) => ({ value: s?.name, label: s?.name }))
      : [];
  }, [adminSettingsDetails]);

  const vsOptionsList = useMemo(() => {
    // Prefer options passed from parent, else derive from settings
    const base =
      Array.isArray(vsAndVCOptionsList) && vsAndVCOptionsList.length > 0
        ? vsAndVCOptionsList
        : Array.isArray(
          settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data,
        )
          ? settingsVehicleSystemsDetails.vehicleSystemsDetails.data.map(
            (vs) => ({
              value: vs?.name,
              label: vs?.name,
            }),
          )
          : [];
    return base;
  }, [vsAndVCOptionsList, settingsVehicleSystemsDetails]);
  const vcOptionsList = useMemo(() => {
    if (!selectedVehicleSystemId) return [];

    // Best source: SCM master list (name -> components) filtered by selected VS name
    const masterList =
      settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data;
    const selectedName = selectedVehicleSystemDetails?.name;
    const matchedFromMaster =
      Array.isArray(masterList) && selectedName
        ? masterList.find((vs) => String(vs?.name) === String(selectedName))
        : null;

    const components =
      (Array.isArray(matchedFromMaster?.components)
        ? matchedFromMaster.components
        : null) ||
      (Array.isArray(selectedVehicleSystemDetails?.components)
        ? selectedVehicleSystemDetails.components
        : []);

    // If parent supplies grouped VS -> VC options, try to use it as a fallback.
    if (components.length === 0) {
      const grouped = Array.isArray(vsAndVCOptionsList)
        ? vsAndVCOptionsList
        : [];
      const matchedGrouped = selectedName
        ? grouped.find((vs) => String(vs?.label) === String(selectedName)) ||
        grouped.find((vs) => String(vs?.value) === String(selectedName))
        : null;
      const groupedComponents =
        matchedGrouped?.vehicleComponentsOptionsList ||
        matchedGrouped?.components ||
        [];
      return Array.isArray(groupedComponents) ? groupedComponents : [];
    }

    return components.map((vc) => ({ value: vc, label: vc }));
  }, [
    selectedVehicleSystemDetails,
    selectedVehicleSystemId,
    settingsVehicleSystemsDetails,
    vsAndVCOptionsList,
  ]);

  // Top counts chips (optional parity)
  // No local counts displayed here; CarCard in parent handles counts.

  // Clear selected VS whenever zone changes to avoid stale selection/components
  useEffect(() => {
    dispatch(selectVehicleSystemAction(null));
  }, [
    dispatch,
    selectedZoneDetails?.position,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
  ]);

  return (
    <Box>
      {/* <Stack direction={{ xs: "column", lg: "row" }} spacing={2}> */}
      {/* Right side: Vehicle Systems and Vehicle Components cards side-by-side */}
      <Box sx={{ flex: 2, display: "flex", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <VehicleSystems
            getZoneVSCountList={getZoneVSCountList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            selectedZoneDetails={selectedZoneDetails}
            variantOptionList={variantOptionList}
            supplierOptionsList={supplierOptionsList}
            vsOptionsList={vsOptionsList}
            filterState={filterState}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            selectedVehicleSystem={selectedVehicleSystem}
            setSelectedVehicleSystem={setSelectedVehicleSystem}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <VehicleComponents
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            variantOptionList={variantOptionList}
            selectedZoneDetails={selectedZoneDetails}
            supplierOptionsList={supplierOptionsList}
            vcOptionsList={vcOptionsList}
            filterState={filterState}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            selectedVehicleSystem={selectedVehicleSystem}
          />
        </Box>
      </Box>
      {/* </Stack> */}
    </Box>
  );
}

export default VlcVehicleSystemsIndex;
